
import { Task } from '@/types/lead';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Check, Trash2, Pencil } from 'lucide-react';
import { useState } from 'react';
import EditTaskDialog from './EditTaskDialog';
import { stripHtml } from '@/utils/htmlUtils';

interface PendingTasksListProps {
  tasks: Task[];
  onCompleteTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  renderAssignee: (assignedTo?: string) => React.ReactNode;
  formatDueDate: (dueDate: Date | string | undefined) => string;
  getPriorityBadge: (priority: any) => React.ReactNode;
  getTaskStatusIndicator: (status: any, dueDate?: Date | string) => React.ReactNode;
}

const PendingTasksList = ({
  tasks,
  onCompleteTask,
  onDeleteTask,
  onUpdateTask,
  renderAssignee,
  formatDueDate,
  getPriorityBadge,
  getTaskStatusIndicator
}: PendingTasksListProps) => {
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ];

  return (
    <div>
      <h4 className="font-medium text-sm mb-3">Pending Tasks ({tasks.length})</h4>
      <div className="space-y-4">
        {tasks.map((task) => (
          <div key={task.id} className="border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-medium">{task.title}</h4>
                {task.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {stripHtml(task.description)}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-2">
                  {getPriorityBadge(task.priority)}
                  {task.dueDate && (
                    <Badge variant="outline" className="flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {formatDueDate(task.dueDate)}
                    </Badge>
                  )}
                  {getTaskStatusIndicator(task.status, task.dueDate)}
                  {renderAssignee(task.assignedTo)}
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setEditingTask(task)}
                  title="Edit task"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onCompleteTask(task.id)}
                  title="Mark as completed"
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onDeleteTask(task.id)}
                  title="Delete task"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editingTask && (
        <EditTaskDialog
          isOpen={!!editingTask}
          setIsOpen={() => setEditingTask(null)}
          task={editingTask}
          onSave={onUpdateTask}
          priorityOptions={priorityOptions}
        />
      )}
    </div>
  );
};

export default PendingTasksList;
